import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router'

import {
  cancelAppointment,
  fetchMyAppointments,
  type MyAppointment,
} from '../api/appointments'
import { requestOtp, verifyOtp, type PatientSession } from '../api/patientAuth'
import { EmptyState } from '../components/EmptyState'
import { Icon } from '../components/Icon'
import {
  PAGE_GUTTER_DESKTOP,
  PageBand,
  PageShell,
} from '../components/PageShell'
import { ApiError } from '../lib/api'
import { getPatientSession, setPatientSession } from '../lib/patientSession'
import { sessionLabel } from '../lib/sessionLabel'

/** Human sentence for each appointment status (never a raw enum). */
const STATUS_SENTENCE: Record<string, string> = {
  requested: 'Awaiting a call back from AMTC to confirm.',
  confirmed: 'Confirmed — check your messages for the details.',
  completed: 'Completed.',
  cancelled: 'Cancelled.',
  declined: 'Could not be arranged.',
}

/**
 * A patient's appointment requests (M14-T08). Memory-only patient sessions
 * (ADR-0021) mean this is OTP-gated like every other patient view: verify phone,
 * then see and cancel your own requests.
 */
export function MyAppointments(): ReactNode {
  const navigate = useNavigate()
  const [phone, setPhone] = useState('')
  const [code, setCode] = useState('')
  const [codeSent, setCodeSent] = useState(false)
  // Reuse the session from a just-submitted request (in-memory only); a cold
  // visit (reload, new tab) starts null and re-verifies.
  const [session, setSession] = useState<PatientSession | null>(() =>
    getPatientSession(),
  )

  const requestMutation = useMutation({
    mutationFn: () => requestOtp(phone.trim()),
    onSuccess: () => setCodeSent(true),
  })
  const verifyMutation = useMutation({
    mutationFn: () => verifyOtp(phone.trim(), code.trim()),
    onSuccess: (s) => {
      setSession(s)
      setPatientSession(s)
    },
  })
  // A stale cached token → re-verify rather than show a load error.
  const authExpired = useCallback(() => {
    setPatientSession(null)
    setSession(null)
  }, [])

  return (
    <PageShell measure={640}>
      <header
        className={`flex items-center gap-1.5 border-b border-line-1 px-3 py-2 ${PAGE_GUTTER_DESKTOP}`}
      >
        <button
          type="button"
          aria-label="Back"
          onClick={() => navigate(-1)}
          className="rounded p-1.5 text-ink-2 hover:bg-bg-soft focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
        >
          <Icon name="chevl" size={18} />
        </button>
        <span className="text-[13px] font-medium text-ink-1">My requests</span>
      </header>
      {/* One band for the body; nothing nested restates the measure and no
          padding moves, so mobile is unchanged by construction. */}
      <PageBand
        className="flex flex-1 flex-col"
        contentClassName="flex flex-1 flex-col"
      >
        {session === null ? (
          <div className="flex flex-col gap-2 px-4 py-5">
            <p className="text-[13px] text-ink-2">
              Verify your phone to see your appointment requests.
            </p>
            <div className="flex gap-2">
              <input
                type="tel"
                aria-label="Phone number"
                placeholder="Phone, e.g. +233…"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="flex-1 rounded-r3 border border-line-2 bg-bg-app px-3 py-2 text-sm text-ink-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
              />
              <button
                type="button"
                disabled={phone.trim().length < 8 || requestMutation.isPending}
                onClick={() => requestMutation.mutate()}
                className="shrink-0 rounded-r3 border border-accent bg-accent-bg px-3 text-[13px] font-medium text-accent-ink disabled:opacity-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
              >
                {requestMutation.isPending
                  ? 'Sending…'
                  : codeSent
                    ? 'Resend'
                    : 'Send code'}
              </button>
            </div>
            {codeSent && (
              <div className="flex gap-2">
                <input
                  type="text"
                  inputMode="numeric"
                  aria-label="Verification code"
                  placeholder="6-digit code"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  className="flex-1 rounded-r3 border border-line-2 bg-bg-app px-3 py-2 text-sm text-ink-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
                />
                <button
                  type="button"
                  disabled={code.trim().length < 4 || verifyMutation.isPending}
                  onClick={() => verifyMutation.mutate()}
                  className="shrink-0 rounded-r3 bg-accent px-4 text-[13px] font-medium text-accent-on disabled:opacity-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
                >
                  Verify
                </button>
              </div>
            )}
            {(requestMutation.isError || verifyMutation.isError) && (
              <p role="alert" className="text-[12px] text-bed-bad">
                Something went wrong. Please try again.
              </p>
            )}
          </div>
        ) : (
          <AppointmentList token={session.token} onAuthExpired={authExpired} />
        )}
      </PageBand>
    </PageShell>
  )
}

function AppointmentList({
  token,
  onAuthExpired,
}: {
  token: string
  onAuthExpired: () => void
}): ReactNode {
  const queryClient = useQueryClient()
  const query = useQuery({
    queryKey: ['my-appointments'],
    queryFn: ({ signal }) => fetchMyAppointments(token, signal),
    retry: (count, err) =>
      err instanceof ApiError && err.status === 401 ? false : count < 2,
  })
  const cancelMutation = useMutation({
    mutationFn: (reference: string) => cancelAppointment(token, reference),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['my-appointments'] }),
  })

  useEffect(() => {
    if (query.error instanceof ApiError && query.error.status === 401) {
      onAuthExpired()
    }
  }, [query.error, onAuthExpired])

  if (query.isPending) {
    return (
      <p className="px-4 py-8 text-[13px] text-ink-3" role="status">
        Loading…
      </p>
    )
  }
  if (query.isError) {
    return (
      <p className="px-4 py-8 text-[13px] text-ink-3" role="alert">
        Couldn&apos;t load your requests.
      </p>
    )
  }
  if (query.data.length === 0) {
    // M17-T10 — the shared EmptyState from T05. Copy unchanged; only the shell
    // moves, so the existing assertion on this sentence still passes.
    return (
      <div className="px-4 py-8">
        <EmptyState title="You have no appointment requests yet." />
      </div>
    )
  }

  return (
    <ul className="flex flex-col gap-2 px-4 py-4">
      {query.data.map((a) => (
        <AppointmentCard
          key={a.reference}
          appointment={a}
          onCancel={() => cancelMutation.mutate(a.reference)}
          cancelling={cancelMutation.isPending}
        />
      ))}
    </ul>
  )
}

function AppointmentCard({
  appointment: a,
  onCancel,
  cancelling,
}: {
  appointment: MyAppointment
  onCancel: () => void
  cancelling: boolean
}): ReactNode {
  const canCancel = a.status === 'requested' || a.status === 'confirmed'
  return (
    <li className="rounded-r3 border border-line-1 bg-bg-app p-3">
      <div className="flex items-center gap-2">
        <span className="flex-1 text-[13.5px] font-semibold text-ink-1">
          {a.specialist_name}
        </span>
        <span className="font-mono text-[11px] text-ink-3">{a.reference}</span>
      </div>
      <div className="text-[12px] text-ink-2">
        {a.specialty} · {a.hospital_name}
      </div>
      <div className="mt-1 inline-flex items-center gap-1 text-[12px] text-ink-2">
        <Icon name="clock" size={12} /> {sessionLabel(a.session)}
      </div>
      <p className="mt-1.5 text-[12px] text-ink-2">
        {STATUS_SENTENCE[a.status] ?? a.status}
        {a.status === 'declined' && a.decline_reason
          ? ` (${a.decline_reason})`
          : ''}
      </p>
      {canCancel && (
        <button
          type="button"
          disabled={cancelling}
          onClick={onCancel}
          className="mt-2 rounded-r2 border border-line-2 px-3 py-1.5 text-[12px] font-medium text-ink-1 hover:bg-bg-soft disabled:opacity-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
        >
          {cancelling ? 'Cancelling…' : 'Cancel request'}
        </button>
      )}
    </li>
  )
}
